import { FunctionComponent, useEffect, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import { useTranslation } from 'react-i18next'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import styles from './addPresenceReservationDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useMutation, useQuery } from '@apollo/client'
import { CREATE_RESERVATION_EVENT } from '@/modules/API/daycare/eventDayHandler.ts'
import { GET_PARENT_LIST } from '@/modules/API/parent/parentHandler.ts'
import { Parent } from '@/types/Parent.ts'
import Loading from '@/components/Loading/Loading.tsx'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { Daycare, DaycareType } from '@/types/Daycare.ts'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage.tsx'
import { Child } from '@/types/Child.ts'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import { EventDay, ReservationEvent, ReservationParent } from '@/types/EventDay.ts'
import { Add } from '@mui/icons-material'
import AddNewParentDialog from './addNewParentDialog/addNewParentDialog.tsx'
import TextArea from '@/components/_custom/TextArea/textArea.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AddPresenceReservationProps {
    open: boolean
    handleClose: () => void
    daycare: Daycare
    event: EventDay
    reservationChildIds: number[]
    reservationParents: ReservationParent[]
    parentOnly: boolean
    reservations: ReservationEvent[]
    refetchAllReservations: () => Promise<void>
}

const AddPresenceReservationDialog: FunctionComponent<AddPresenceReservationProps> = ({
    open,
    handleClose,
    daycare,
    event,
    reservationChildIds,
    reservationParents,
    parentOnly,
    reservations,
    refetchAllReservations
}: AddPresenceReservationProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [daycareComment, setDaycareComment] = useState<string>('')

    const [parentsList, setParentsList] = useState<Array<Parent>>([])

    const {
        loading: loadingParentList,
        data: dataParentList,
        error: errorParentList
    } = useQuery(GET_PARENT_LIST, {
        variables: {
            daycare_id: daycare.id
        },
        fetchPolicy: 'network-only'
    })

    useEffect(() => {
        if (dataParentList && dataParentList.getParentList) {
            setParentsList(dataParentList.getParentList)
        }
    }, [dataParentList])

    const [selectedFamily, setSelectedFamily] = useState<string>('')

    const familiesOptions: SelectOptionsDeprecated = [
        {
            options: parentsList
                .filter((parent: Parent): boolean => parent.id !== 0)
                .sort((a: Parent, b: Parent): number => a.lastname.localeCompare(b.lastname))
                .map((parent: Parent): SelectOptionDeprecated => {
                    return {
                        value: parent.id.toString(),
                        name: parent.lastname ? parent.lastname + ' ' + parent.firstname : parent.email
                    }
                })
        }
    ]

    const [reservationChildOptions, setReservationChildOptions] = useState<SelectOptionsDeprecated>([])
    const [selectedReservationChilds, setSelectedReservationChilds] = useState<string[]>([])

    const [reservationParentOptions, setReservationParentOptions] = useState<SelectOptionsDeprecated>([])
    const [selectedReservationParents, setSelectedReservationParents] = useState<string[]>([])

    const [isEditPresence, setEditPresence] = useState<boolean>(false)

    const [placesBooked, setPlacesBooked] = useState<number>(0)
    const [attendantBooked, setAttendantBooked] = useState<number>(0)

    function handleSelectFamily(id: string): void {
        const parentSelected: Parent | undefined = parentsList.find((p: Parent): boolean => p.id.toString() === id)
        setSelectedFamily(id)

        if (!parentSelected) return

        const parentChilds: Child[] = parentSelected.childs

        setReservationChildOptions([
            {
                options: parentChilds.map((c: Child): SelectOptionDeprecated => {
                    return {
                        value: c.id.toString(),
                        name: c.firstname
                    }
                })
            }
        ])
        const childReservedList: string[] = parentChilds
            .filter((c: Child): boolean => reservationChildIds.includes(c.id))
            .map((c: Child): string => c.id.toString())
        setSelectedReservationChilds(childReservedList)

        const parentReservedList: string[] =
            reservationParents.length >= 1
                ? reservationParents
                      .filter((rp: ReservationParent): boolean => rp.parentId === parentSelected.id)
                      .map((rp: ReservationParent): string => rp.name)
                : event.parentOnly
                  ? [parentSelected.firstname]
                  : []

        setSelectedReservationParents(parentReservedList)
        setReservationParentOptions([
            {
                options:
                    reservationParents.length >= 1
                        ? reservationParents
                              .filter((rp: ReservationParent): boolean => rp.parentId === parentSelected.id)
                              .map((rp: ReservationParent): SelectOptionDeprecated => {
                                  return {
                                      value: rp.name,
                                      name: rp.name
                                  }
                              })
                        : event.parentOnly
                          ? [{ value: parentSelected.firstname, name: parentSelected.firstname }]
                          : []
            }
        ])

        setEditPresence(childReservedList.length > 0 || reservationParents.length >= 1)
        setPlacesBooked(event.placesBooked - childReservedList.length)
        setAttendantBooked(event.attendantBooked - parentReservedList.length)

        setDaycareComment(
            reservations.find((r: ReservationEvent): boolean => r.parentId === parentSelected.id)?.daycareComment || ''
        )
    }

    const [showAddNewParent, setShowAddNewParent] = useState<boolean>(false)

    function handleAddParent(parentName: string): void {
        setSelectedReservationParents((prev: string[]): string[] => [...prev, parentName])
        setReservationParentOptions(
            (prev: SelectOptionsDeprecated): SelectOptionsDeprecated => [
                ...prev,
                { options: [{ value: parentName, name: parentName }] }
            ]
        )
        setShowAddNewParent(false)
    }

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    const [createReservation, { loading: loadingCreate }] = useMutation(CREATE_RESERVATION_EVENT, {
        variables: {
            event_id: event.id,
            childs_id: selectedReservationChilds.map((child: string): number => parseInt(child)),
            adults: selectedReservationParents
                .filter((adult: string): boolean => adult !== '')
                .map((adult: string): { name: string } => {
                    return {
                        name: adult
                    }
                }),
            daycare_id: daycare.id,
            parent_id: Number(selectedFamily),
            comment: daycareComment
        },
        onCompleted: () => {
            showNotificationSuccess(t('reservationCreated'))

            refetchAllReservations()

            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorReservationCreated'))
        }
    })

    const disabledButton: boolean =
        (!isEditPresence && selectedReservationChilds.length === 0 && selectedReservationParents.length === 0) ||
        loadingCreate

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={t('addPresence')} />
            <DialogBody className={styles.DialogBody}>
                {(loadingParentList || loadingCreate) && <Loading overall={false} />}
                {dataParentList && parentsList.length > 0 && (
                    <div className={styles.Container}>
                        <div className={styles.Input}>
                            <SelectUnique
                                label={isRpeOrLaep ? t('publicList') : t('familiesList')}
                                className={styles.Selector}
                                selectOptions={familiesOptions}
                                selectValue={selectedFamily}
                                setSelectValue={handleSelectFamily}
                            />
                        </div>
                        {selectedFamily && (
                            <>
                                {!parentOnly && (
                                    <div className={styles.Input}>
                                        <SelectMultiple
                                            fullWidth
                                            label={t('childs')}
                                            className={styles.Selector}
                                            selectOptions={reservationChildOptions}
                                            setSelectValues={setSelectedReservationChilds}
                                            selectValues={selectedReservationChilds}
                                        />
                                    </div>
                                )}
                                <div className={styles.Input}>
                                    <SelectMultiple
                                        fullWidth
                                        label={isRpeOrLaep ? t('adult') : t('parent')}
                                        className={styles.Selector}
                                        selectOptions={reservationParentOptions}
                                        setSelectValues={setSelectedReservationParents}
                                        selectValues={selectedReservationParents}
                                    />
                                    <div className={styles.TextInput} onClick={() => setShowAddNewParent(true)}>
                                        <Add className={styles.Icon} />
                                        <p className={styles.Text}>{t('addAdult')}</p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {errorParentList && <ErrorMessage error={t('generalError')} />}
                {placesBooked + selectedReservationChilds.length > event.places && (
                    <ErrorMessage
                        error={
                            t('exceedYourChildCapacity') +
                            (placesBooked + selectedReservationChilds.length) +
                            '/' +
                            event.places
                        }
                    />
                )}
                {attendantBooked + selectedReservationParents.length > event.attendant && (
                    <ErrorMessage
                        error={
                            t('exceedYourAdultCapacity') +
                            (attendantBooked + selectedReservationParents.length) +
                            '/' +
                            event.attendant
                        }
                    />
                )}
                {!disabledButton && (
                    <>
                        <div className={styles.BreakLine}></div>
                        <div className={styles.TextAreaInput}>
                            <p className={styles.TextAreaTitle}>{t('comment')}</p>
                            <TextArea
                                placeholder={t('comment')}
                                maxCount={200}
                                className={styles.TextArea}
                                value={daycareComment}
                                onChange={setDaycareComment}
                            />
                        </div>
                    </>
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={isEditPresence ? 'update' : 'add'}
                        onClick={createReservation}
                        className={styles.Button}
                        disabled={disabledButton}
                    />
                </div>
                {showAddNewParent && (
                    <AddNewParentDialog
                        open={showAddNewParent}
                        handleClose={() => setShowAddNewParent(false)}
                        handleAddParent={handleAddParent}
                    />
                )}
            </DialogBody>
        </Dialog>
    )
}

export default AddPresenceReservationDialog
